import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Import types and helpers
import { getItemIdentifier } from "./_helpers/item-identifier";
import { applyFilters, filterItems, paginateItems, sortItems } from "./_helpers/list-operations";
import {
  CustomSelectionAction,
  DataListProps,
  ItemWithIdentifier,
  ListActions,
  ListFeatures,
  ServerPaginationState,
  SortOption,
} from "./types";

// Import components
import { DataListContent } from "./_components/data-list-content";
import { DataListFooter } from "./_components/data-list-footer";
import { DataListHeader } from "./_components/data-list-header";

// Re-export types and helpers for backward compatibility
export { getItemIdentifier };
export type {
  CustomSelectionAction,
  DataListProps,
  ItemWithIdentifier,
  ListActions,
  ListFeatures,
  ServerPaginationState,
  SortOption,
};

export function DataList<TData extends ItemWithIdentifier = ItemWithIdentifier>({
  className,
  data = [],
  itemRenderer,
  features = {
    search: true,
    pagination: true,
    multiSelection: false,
    sorting: false,
  },
  isLoading = false,
  isError = false,
  searchPlaceholder,
  searchTerm,
  selection = [],
  customSelectionActions,
  itemActions,
  filters = [],
  filterValues = {},
  sortingOptions,
  initialSorting = [],
  sortingMapping = {},
  serverSidePagination = false,
  paginationState,
  totalItems,
  heightMode = "content",
  itemLayout = "wrap",
  containerClassName,
  itemClassName,
  headerComponent,
  footerComponent,
  emptyStateComponent,
  loadingComponent,
  errorComponent,
  actions,
  visibility,
  createButtonLink,
  createButtonText,
}: DataListProps<TData>) {
  const { t } = useTranslation("common");
  const { deviceType } = useDevice();

  // Check visibility constraints
  const isVisible = useMemo(() => {
    if (!visibility) return true;
    if (!deviceType) return false;
    return visibility.includes(deviceType);
  }, [visibility, deviceType]);

  // Stable references for actions to prevent re-renders
  const actionsRef = useRef(actions);

  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const [internalSearchTerm, setInternalSearchTerm] = useState(searchTerm || "");
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string[]>>({});
  const [internalSorting, setInternalSorting] = useState<SortOption[]>(initialSorting);
  const [internalPaginationState, setInternalPaginationState] = useState<ServerPaginationState>({
    page: 1,
    pageSize: 10,
  });
  const [internalSelection, setInternalSelection] = useState<string[]>(selection);
  const justClearedRef = useRef(false);

  // Debounce the search term for the actual search functionality
  const debouncedSearchTerm = useDebounce(internalSearchTerm, 300);

  // Effect to handle debounced search - only for uncontrolled search
  useEffect(() => {
    // Only trigger onSearchChange if the search term is managed internally (uncontrolled)
    if (searchTerm === undefined && !justClearedRef.current) {
      actionsRef.current?.onSearchChange?.(debouncedSearchTerm);
    }

    justClearedRef.current = false;
  }, [debouncedSearchTerm, searchTerm]);

  const currentSearchTerm = searchTerm !== undefined ? searchTerm : internalSearchTerm;
  const currentFilterValues = filterValues || internalFilterValues;
  const currentSorting = internalSorting;
  const currentPaginationState = paginationState || internalPaginationState;
  const currentSelection = selection.length > 0 ? selection : internalSelection;

  // Process data if not using server-side operations
  const processedData = useMemo(() => {
    if (serverSidePagination) {
      return data;
    }

    let result = [...data];

    // Apply search
    if (features.search && currentSearchTerm) {
      result = filterItems(result, currentSearchTerm);
    }

    // Apply filters
    if (Object.keys(currentFilterValues).length > 0) {
      result = applyFilters(result, currentFilterValues);
    }

    // Apply sorting
    if (features.sorting && currentSorting.length > 0) {
      result = sortItems(result, currentSorting);
    }

    return result;
  }, [
    data,
    features.search,
    features.sorting,
    currentSearchTerm,
    currentFilterValues,
    currentSorting,
    serverSidePagination,
  ]);

  // Calculate pagination
  const paginatedData = useMemo(() => {
    if (!features.pagination || serverSidePagination) {
      return processedData;
    }

    return paginateItems(processedData, currentPaginationState.page, currentPaginationState.pageSize);
  }, [processedData, features.pagination, serverSidePagination, currentPaginationState]);

  const effectiveTotalItems = totalItems ?? (serverSidePagination ? undefined : processedData.length);
  const selectedCount = currentSelection.length;
  const activeFilterCount = Object.values(currentFilterValues).reduce((count, values) => count + values.length, 0);
  const isSelectionEnabled = features.multiSelection || !!actions?.onSelectionChange;

  // Event handlers
  const handleFilterChange = (filterId: string, values: string[]) => {
    if (actionsRef.current?.onFilterChange) {
      actionsRef.current.onFilterChange(filterId, values);
    } else {
      setInternalFilterValues((prev) => ({
        ...prev,
        [filterId]: values,
      }));
    }
  };

  const handleSearchChange = (value: string) => {
    if (searchTerm !== undefined) {
      // Controlled search - call the parent's callback directly
      actionsRef.current?.onSearchChange?.(value);
    } else {
      // Uncontrolled search - update internal state (will trigger debounced effect)
      setInternalSearchTerm(value);
    }
  };

  const handleClearAllFilters = () => {
    const clearedFilters = filters.reduce(
      (acc, filter) => {
        acc[filter.id] = [];
        return acc;
      },
      {} as Record<string, string[]>,
    );

    if (actionsRef.current?.onFilterChange) {
      Object.keys(clearedFilters).forEach((filterId) => {
        actionsRef.current?.onFilterChange?.(filterId, clearedFilters[filterId]);
      });
    } else {
      setInternalFilterValues(clearedFilters);
    }
  };

  const handleClearSearch = () => {
    setInternalSearchTerm("");
    justClearedRef.current = true;
    actionsRef.current?.onSearchChange?.("");
  };

  const handleSortingChange = (sorting: SortOption[]) => {
    setInternalSorting(sorting);

    if (actionsRef.current?.onSortingChange) {
      const mappedSorting = sorting.flatMap((sort) => {
        const mappedFields = sortingMapping[sort.field];
        if (mappedFields && mappedFields.length > 0) {
          return mappedFields.map((field) => ({
            field,
            direction: sort.direction,
          }));
        }
        return [sort];
      });

      actionsRef.current.onSortingChange(mappedSorting);
    }
  };

  const handlePaginationChange = (state: ServerPaginationState) => {
    if (!paginationState) {
      setInternalPaginationState(state);
    }
    actionsRef.current?.onPaginationChange?.(state);
  };

  const handleClearSelection = () => {
    if (!actionsRef.current?.onSelectionChange) {
      setInternalSelection([]);
    }
    actionsRef.current?.onClearSelection?.();
  };

  const getContainerStyle = () => {
    switch (heightMode) {
      case "viewport":
        return {
          height: "calc(100vh - 5rem)",
          maxHeight: "calc(100vh - 5rem)",
          minHeight: "calc(100vh - 5rem)",
        };
      case "content":
        return {};
      case "custom":
        return {};
      default:
        return {};
    }
  };

  const getContainerClasses = () => {
    switch (heightMode) {
      case "viewport":
        return "flex flex-col";
      case "content":
        return "flex flex-col min-h-0";
      case "custom":
        return "flex flex-col h-full";
      default:
        return "flex flex-col h-full";
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={cn(getContainerClasses(), className)} style={getContainerStyle()}>
      <DataListHeader
        headerComponent={headerComponent}
        features={features}
        filters={filters}
        activeFilterCount={activeFilterCount}
        searchTerm={currentSearchTerm}
        searchPlaceholder={searchPlaceholder ?? t("Search")}
        totalItems={effectiveTotalItems}
        serverSidePagination={serverSidePagination}
        paginationState={currentPaginationState}
        isLoading={isLoading}
        onPaginationChange={handlePaginationChange}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        currentFilterValues={currentFilterValues}
        sortingOptions={sortingOptions}
        currentSorting={currentSorting}
        onSortingChange={handleSortingChange}
        createButtonLink={createButtonLink}
        createButtonText={createButtonText ?? t("common:Add")}
      />

      <DataListContent
        data={paginatedData}
        itemRenderer={itemRenderer}
        isLoading={isLoading}
        isError={isError}
        activeFilterCount={activeFilterCount}
        searchTerm={currentSearchTerm}
        itemLayout={itemLayout}
        containerClassName={containerClassName}
        itemClassName={itemClassName}
        onItemClick={(item) => actionsRef.current?.onItemClick?.(item)}
        itemActions={itemActions}
        onClearAllFilters={handleClearAllFilters}
        onClearSearch={handleClearSearch}
        emptyStateComponent={emptyStateComponent}
        loadingComponent={loadingComponent}
        errorComponent={errorComponent}
        selection={currentSelection}
        features={features}
      />

      <DataListFooter
        footerComponent={footerComponent}
        isSelectionEnabled={isSelectionEnabled}
        selectedCount={selectedCount}
        customSelectionActions={customSelectionActions}
        selection={currentSelection}
        serverSidePagination={serverSidePagination}
        totalItems={effectiveTotalItems}
        onClearSelection={handleClearSelection}
        features={features}
      />
    </div>
  );
}
